import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  TextField,
  Box,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { RETRIEVE_PII_WITH_UPLOAD_FN } from "./bondRetrieveSharedPropTypes";
import { RETRIEVAL_DOCUMENT_FIELD_NAMES } from "../../../../features/bonds/shared/bondDocumentUploadFieldConfig";
import useBondDocumentUploadState from "../../../../features/bonds/shared/hooks/useBondDocumentUploadState";

const RetrievalDocumentUploadForm = ({
  data,
  updateData,
  globalData,
  role,
  pii_access,
  isDisabled,
  uploadToADLS,
  sections,
  noteDisabled = true,
  indemnityViewDisclaimer = false,
}) => {
  const {
    formValues,
    loading,
    handleFileChange,
    handleChange,
    removeConfirmOpen,
    handleRemoveClick,
    handleRemoveConfirm,
    handleRemoveCancel,
  } = useBondDocumentUploadState({
    data,
    updateData,
    fieldNames: RETRIEVAL_DOCUMENT_FIELD_NAMES,
    uploadToADLS,
    scrollOnMount: true,
  });

  const [indemnityDisclaimerOpen, setIndemnityDisclaimerOpen] = useState(false);
  const [indemnityPendingAction, setIndemnityPendingAction] = useState(null);

  const noFilePlaceholders = new Set(["No file uploaded", "No file selected.", ""]);
  const getFileName = (n) => formValues[`${n}FileName`] || data?.[`${n}FileName`];
  const hasFile = (n) =>
    formValues[n] ||
    formValues[`${n}Link`] ||
    data?.[`${n}Link`] ||
    (getFileName(n) && !noFilePlaceholders.has(String(getFileName(n)).trim()));

  const getUploadStatusLabel = (name) => {
    if (loading[name]) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={16} />
          <span>Uploading...</span>
        </Box>
      );
    }
    if (hasFile(name)) {
      return formValues[name]?.name || getFileName(name);
    }
    return "No File Uploaded.";
  };

  const renderViewDocumentButton = (name) => {
    const link = formValues[`${name}Link`] || data?.[`${name}Link`];
    if (name === "indemnity" && indemnityViewDisclaimer) {
      return (
        <Button
          variant="outlined"
          color="secondary"
          fullWidth
          sx={{ mt: 2 }}
          onClick={() => {
            setIndemnityPendingAction({ type: "view", link });
            setIndemnityDisclaimerOpen(true);
          }}
        >
          View Document
        </Button>
      );
    }
    return (
      <Button
        variant="outlined"
        color="secondary"
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        fullWidth
        sx={{ mt: 2 }}
      >
        View Document
      </Button>
    );
  };

  const renderDocumentSection = (label, name) => (
    <Box key={name}>
      <Typography variant="body1" className="mb-2">
        {label}
      </Typography>
      <Button
        disabled={isDisabled}
        variant="outlined"
        component="label"
        fullWidth
      >
        Browse...{" "}
        <input type="file" hidden name={name} onChange={handleFileChange} />
      </Button>
      <Typography variant="body2" color="textSecondary" mt={1}>
        {getUploadStatusLabel(name)}
      </Typography>
      {hasFile(name) && (formValues[`${name}Link`] || data?.[`${name}Link`]) &&
        renderViewDocumentButton(name)}
      {(formValues[name] || formValues[`${name}Link`] || data?.[`${name}Link`]) &&
        !["bond", "bondApp", "indemnity"].includes(name) && (
          <Button
            variant="outlined"
            color="error"
            fullWidth
            onClick={() => handleRemoveClick(name)}
            disabled={isDisabled}
            sx={{ mt: 1 }}
          >
            Remove Attachment
          </Button>
        )}
    </Box>
  );

  const showCreditDocument =
    role === "carrier_team_member" ||
    (role === "super_user" && pii_access) ||
    (role === "internal_user" && pii_access);

  return (
    <Box
      className="bg-gray-100 p-6 rounded-lg shadow-md max-w-3xl mx-auto mt-8"
      sx={{ border: "1px solid #e0e0e0" }}
    >
      <Typography variant="h5" className="font-bold mb-4">
        Document Upload
      </Typography>

      <Box className="grid grid-cols-1 gap-6">
        {sections.map(({ label, name }) => {
          if (name === "creditDocument" && !showCreditDocument) {
            return null;
          }
          return renderDocumentSection(label, name);
        })}

        <TextField
          disabled={noteDisabled && isDisabled}
          label="Note"
          name="note"
          value={formValues.note || data?.note || ""}
          onChange={handleChange}
          fullWidth
          multiline
          rows={4}
          variant="outlined"
          sx={{ mb: 3 }}
        />
      </Box>

      <Dialog
        open={indemnityDisclaimerOpen}
        onClose={() => {
          setIndemnityDisclaimerOpen(false);
          setIndemnityPendingAction(null);
        }}
      >
        <DialogTitle>Indemnity Agreement</DialogTitle>
        <DialogContent>
          <Typography>
            This Indemnity Agreement does not need to be signed or submitted to Probate Bond Pros. This Indemnity Agreement is viewable and downloadable for your records.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (indemnityPendingAction?.link) {
                window.open(indemnityPendingAction.link, "_blank", "noopener,noreferrer");
              }
              setIndemnityDisclaimerOpen(false);
              setIndemnityPendingAction(null);
            }}
            color="primary"
            variant="contained"
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={removeConfirmOpen} onClose={handleRemoveCancel}>
        <DialogTitle>Remove Attachment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to remove this document? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRemoveCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleRemoveConfirm} color="error" variant="contained">
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

RetrievalDocumentUploadForm.propTypes = {
  ...RETRIEVE_PII_WITH_UPLOAD_FN,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  noteDisabled: PropTypes.bool,
  indemnityViewDisclaimer: PropTypes.bool,
};

export default RetrievalDocumentUploadForm;
